import Select, { components } from 'react-select'
import { STATUS_VALUES, parseStatusFilter, serialiseStatusFilter, statusFilterLabel } from './statusFilter'
import { fieldError, blockSpecialNumberKeys, blockSpecialNumberPaste, blockSpecialNumberDrop } from './fieldLimits'

// Every number in this file comes from files/sow2/spec/arv.md sections 4 and 4a, measured out of
// the 18.08 export by coordinate rather than read off a render. The frame is named beside each row
// there; the constants below are so the same value is not typed six times.
const FIELD_H = 39
const FIELD_R = 3.5
const BORDER = '#C6CFC6'
const BORDER_FOCUS = '#4CBC18'
const BORDER_ERROR = '#D85263'

const labelCls = 'block text-[13px] text-brand-secondary mb-[5px]'

// Row order, used to pick which single field's pill to show when more than one is invalid at
// once. Same mechanism as SearchCriteria.jsx's FIELD_ORDER — read that file for the reasoning.
const FIELD_ORDER = ['radius', 'time']

// The 79px fields centre their value; the 99px ones left-align it at 9.5px. That is not a stylistic
// choice — both are measured, and the difference is what makes `1` sit in the middle of Radius
// while `Top 10` starts at the left edge of Show.
// Comment Set #2, item 3.4: values and the Min/Max placeholders are weight 500 in the export —
// the 18.08 export this screen was first measured from collapses everything below 700 to
// "regular" and missed it (files/sow2-section-lean.svg, `Min`/`Max`/`Top 10`/`250` text nodes).
const numberFieldCls = `
  shrink-0 w-[79px] h-[39px] rounded-[3.5px] border bg-white
  text-[14px] font-medium text-brand-dark text-center
  focus:outline-none px-[6px]
  placeholder:text-brand-placeholder placeholder:font-medium
`.trim()

// The border is a class and not an inline style, because focus has to be able to win. An inline
// borderColor beats every Tailwind focus: variant, so a field styled that way would keep its resting
// edge while the caret sits in it — measured once already on this screen, which is why the rule is
// written down here rather than left to the next reader to rediscover.
//
// An invalid field stays red while focused. The design draws the error state focused-out only, so
// this is a reading rather than a measurement: a border that turns green the moment the user clicks
// into the field to fix the value would take the mark away exactly when it is being acted on.
const fieldBorderCls = (error) => error
  ? 'border-[#D85263] focus:border-[#D85263]'
  : 'border-[#C6CFC6] hover:border-[#4CBC18] focus:border-[#4CBC18]'

// The message under a field: h 17.5, r 3.75, bg #FDEDEF, border and text #D85263 at 12px, left edge
// flush with its own field and 6.7px below it (frames btn_80-btn_84).
//
// Absolutely positioned on purpose. The pill is wider than the field it belongs to — 231.5 under a
// 79px Radius — so it overflows to the right by design, and taking it out of flow is also what
// stops the whole filter row jumping down by 24px the moment a value goes out of range.
function FieldError({ message }) {
  if (!message) return null
  return (
    <span
      role="alert"
      className="absolute left-0 z-10 whitespace-nowrap rounded-[3.75px] border text-[12px] leading-[17.5px] px-[13.8px]"
      style={{ top: `calc(100% + 6.7px)`, height: 17.5, background: '#FDEDEF', borderColor: BORDER_ERROR, color: BORDER_ERROR }}
    >
      {message}
    </span>
  )
}

// isFocused is read, and it has to be. Setting the `border` shorthand overwrites the borderColor
// react-select computes from its own focus state, and `boxShadow: 'none'` removes the ring it draws
// instead — between them the three menu controls on this screen had a hover style and no focus style
// at all, so a keyboard user tabbing along the filter row had nothing telling them where they were.
// The colour is the same #4CBC18 the drawn focus state uses on the Price field (spec section 4).
const selectControlBase = (base, state, { width }) => ({
  ...base,
  width,
  minHeight: FIELD_H,
  height: FIELD_H,
  border: `1px solid ${state.isFocused ? BORDER_FOCUS : BORDER}`,
  borderRadius: FIELD_R,
  boxShadow: 'none',
  fontSize: 14,
  cursor: 'pointer',
  backgroundColor: 'white',
  '&:hover': { borderColor: BORDER_FOCUS },
})

const menuBase = (base) => ({
  ...base,
  width: 99,
  borderRadius: FIELD_R,
  border: `1px solid ${BORDER_FOCUS}`,
  backgroundColor: 'white',
  overflow: 'hidden',
})

// Show carries its own geometry from the spec table (files/sow2/spec/arv.md section 4): field
// 99x39 r 3.5 with a #C6CFC6 border, menu 99x111 r 3.5 with a #4CBC18 one, and Custom in #8A9A85.
const showSelectStyles = {
  control: (base, state) => selectControlBase(base, state, { width: 99 }),
  valueContainer: (base) => ({ ...base, paddingLeft: 9.5, paddingRight: 0 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  indicatorSeparator: () => ({ display: 'none' }),
  input: (base) => ({ ...base, caretColor: 'transparent' }),
  // Comment Set #1, ARV 6.2 and 6.3 — both are one arithmetic problem, and it is why the caret sits
  // where it does. The field is 99 wide (drawn) with a 1px border each side and the value inset 9.5
  // from the left (drawn: `filters-table/.../input_170` starts its text at x+9.5). react-select's
  // own defaults then took the rest: 36px for the dropdown indicator and a 2px side margin on the
  // value, leaving 47.5 for the text. Measured in Figtree 14 on 02.09: `Custom` is 49.71 and clipped
  // by 2, `2 selected` is 64.84 and — the placeholder defaulting to `white-space: normal` — wrapped
  // onto a second line the 39px control then cut in half.
  //
  // The margin goes to zero, which is what puts the value on the drawn 9.5 rather than 11.5, and the
  // indicator's 8px padding goes with it: the chevron path is inset 4.2 inside its own 20px box, so
  // it still stands off the border. That leaves 97 - 20 - 9.5 = 67.5, and the widest label this
  // field can hold is `2 selected` at 64.84. Nothing longer fits at 14px in 99, which is why
  // statusFilterLabel names a single status rather than listing two.
  //
  // Comment Set #3, item 3 (letter 57, his own words: "increase spacing at what you believe is
  // best"): the chevron reads too close to the field's right border. `padding` above stays 0 —
  // giving it back any padding shrinks the 67.5 budget the paragraph above spent on fitting
  // `2 selected`, and 64.84 leaves only 2.66px of room to give. `transform` moves the chevron 4px
  // left instead: a paint-time shift with no layout width, so the box the indicator reserves — and
  // therefore the text budget above — is untouched.
  dropdownIndicator: (base) => ({ ...base, padding: 0, transform: 'translateX(-4px)' }),
  // Custom is the only value the control ever shows that is not in the menu, so it is also the only
  // one that can be greyed by looking at the value alone.
  singleValue: (base, state) => ({
    ...base,
    margin: 0,
    fontSize: 14,
    fontWeight: 500,
    color: state.data?.value === 'Custom' ? '#8A9A85' : '#17270D',
  }),
  menu: menuBase,
  menuList: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
  // Comment Set #3, item 4: `isSelected`/`isFocused` painted a background the instant the menu
  // opened, before the cursor ever reached it -- neither is a hover signal (isSelected marks "this
  // is the current value", isFocused is react-select's own keyboard-nav position, seeded on open).
  // `&:hover` is real CSS, so only the actual cursor can trigger it -- the same mechanism
  // `selectControlBase` above already uses for the control's own hover. `:active` is overridden too,
  // or react-select's default blue mousedown flash survives the `...base` spread.
  option: (base) => ({
    ...base,
    backgroundColor: '#fff',
    color: '#17270D',
    fontSize: 14,
    cursor: 'pointer',
    ':active': { backgroundColor: '#f8fef4' },
    '&:hover': { backgroundColor: '#f8fef4' },
  }),
}

// Status: same panel as Show (99x111, r 3.5, #4CBC18), three rows 32px apart, the name on the left
// at 14px and the checkbox on the right, 13x13 r 1.5.
const statusSelectStyles = {
  ...showSelectStyles,
  // The label is rendered through the placeholder, which is why it needs the value's colour rather
  // than a placeholder's. See controlShouldRenderValue below for why it goes there at all.
  //
  // And why it needs the four properties react-select gives singleValue and withholds here: a
  // placeholder is a word, so its defaults are `white-space: normal` with no overflow handling, and
  // `2 selected` in a 99px field wrapped rather than clipped. The single line is the fix; the
  // ellipsis is the fallback, so a label longer than the field degrades to `2 select…` instead of
  // being sliced through the middle horizontally.
  placeholder: (base) => ({
    ...base,
    margin: 0,
    fontSize: 14,
    fontWeight: 500,
    color: '#17270D',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  // Same fix as showSelectStyles.option above, same reasoning: real `&:hover` instead of
  // react-select's own isFocused, which this field also seeded on menu open with no cursor
  // involved. isSelected never drove this field's background -- the checkbox already carries
  // selection (StatusCheckbox, isSelected) -- so there was nothing to remove there.
  option: (base) => ({
    ...base,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    padding: '0 11px 0 9.5px',
    backgroundColor: '#fff',
    color: '#17270D',
    fontSize: 14,
    cursor: 'pointer',
    ':active': { backgroundColor: '#f8fef4' },
    '&:hover': { backgroundColor: '#f8fef4' },
  }),
  menuList: (base) => ({ ...base, paddingTop: 7.5, paddingBottom: 7.5 }),
}

// The mock draws all three boxes ticked, so it shows one state and not two. Ticked is measured —
// 13x13, r 1.5, fill #F3FFEB, stroke #4CBC18 — and unticked is derived from the row's own border
// token rather than invented: white on #C6CFC6, the same edge every field in this row carries. The
// tick mark itself is drawn here because the two fills alone (pale green against white at 13px)
// are not a difference a person can see across a table.
function StatusCheckbox({ checked }) {
  return (
    <span
      className="flex items-center justify-center shrink-0"
      style={{
        width: 13, height: 13, borderRadius: 1.5,
        background: checked ? '#F3FFEB' : '#fff',
        border: `1px solid ${checked ? BORDER_FOCUS : BORDER}`,
      }}
    >
      {checked && (
        <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden="true">
          <path d="M1.5 4.6L3.4 6.5L7.5 2.4" stroke={BORDER_FOCUS} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  )
}

const StatusOption = (props) => (
  <components.Option {...props}>
    <span className="flex w-full items-center justify-between">
      <span>{props.label}</span>
      <StatusCheckbox checked={props.isSelected} />
    </span>
  </components.Option>
)

// Custom is a display value, not an option. His answer OC 3: the field shows Custom once the user
// has edited the table, and picking a Top N again narrows the window without touching the
// exclusions. Feeding it to react-select as a value with no matching option is exactly right — the
// control renders the label and the menu still lists only the real choices.
const CUSTOM_OPTION = { value: 'Custom', label: 'Custom' }

const STATUS_OPTIONS = STATUS_VALUES.map((v) => ({ value: v, label: v }))

export default function ComparableFilterRow({
  filters, criteria, onFiltersChange, onFiltersBlur, onCriteriaChange, onCriteriaBlur,
  showOptions = [], showValue, isCustomShow = false, onShowChange,
}) {
  const set = (field) => (e) => onFiltersChange({ ...filters, [field]: e.target.value })

  // Radius and Time write to `criteria`, not to `filters`, and they are filters all the same.
  // arv.filters in reilm-be modules/properties/model.js is a fixed set of seven keys with no room
  // for them, while arv.criteria has held radiusMiles and monthsWithin since before this MR — so
  // moving the fields under the map is a move on screen only, and nothing had to change in the
  // schema. What did change is what the two values mean: they no longer travel to the provider.
  // Radius never did (the RentCast call has `radius: 1` hardcoded, arvFetch.js), and Time now goes
  // out fixed at its maximum so this field can narrow the answer locally, which is his own decision
  // — "Radius фіксований 1 миля, поле 0.4-1.0, нижчі значення фільтрують локально".
  const setCriteria = (field) => (e) => onCriteriaChange({ ...criteria, [field]: e.target.value })

  const selectedStatuses = parseStatusFilter(filters.status)

  const radiusError = fieldError('radius', criteria?.radiusMiles)
  const timeError = fieldError('time', criteria?.monthsWithin)

  const fieldErrorsByName = { radius: radiusError, time: timeError }
  const shownErrorField = FIELD_ORDER.find((field) => fieldErrorsByName[field])

  return (
    <div className="flex items-end justify-between mt-6">

      {/* Left — filters. Order and widths are the spec's, left to right: Show 99, Status 99, then
          four 79px fields, 15px apart. `justify-between` and the outer `gap-x` are gone from this
          div now that it is single-child — the Included/Excluded chips that used to share it moved
          out to IncludedExcludedToggle.jsx. */}
      <div className="flex items-end w-[885px]">
        <span className='flex items-end gap-x-[15px]'>
          {/* Show — replaces the Load More Comps button under the table */}
          <div className="flex flex-col shrink-0" style={{ width: 99 }}>
            <span className={labelCls}>Show</span>
            {/* No fallback to showOptions[0], and no placeholder either. That fallback was the lie:
                a stored `visible` the menu could not offer - every value Load More had persisted,
                and any Top N larger than the filtered set - showed the first option here while the
                table went on cutting at the stored number. `placeholder="Top 10"` was the same lie
                one layer down, since react-select renders the placeholder whenever the value misses,
                so deleting only the `??` would have left the first option's label hardcoded in its
                place. showValue arrives normalised against these very options (Arv.jsx
                normaliseShow), so a miss cannot happen while both come off the same total in the
                same render - that coupling at the call site is now the only way to break this
                field, and what shows if anyone does is react-select's own empty state rather than a
                number about the window. */}
            <Select
              value={isCustomShow
                ? CUSTOM_OPTION
                : showOptions.find(o => o.value === showValue) ?? null}
              options={showOptions}
              onChange={(opt) => opt && onShowChange?.(opt.value)}
              styles={showSelectStyles}
              menuPortalTarget={document.body}
              isSearchable={false}
            />
          </div>

          {/* Status — three checkboxes, All only as a label */}
          <div className="flex flex-col shrink-0" style={{ width: 99 }}>
            <span className={labelCls}>Status</span>
            {/* controlShouldRenderValue={false} with the summary passed as the placeholder, because
                the field is 99px wide and react-select's own multi-value rendering is one chip per
                selection - three chips in 99px is unreadable, and the design draws a single word.
                This is react-select's documented way to take over that rendering without replacing
                the whole ValueContainer, and the placeholder style above gives it the value colour
                so it does not read as an empty field.

                closeMenuOnSelect={false} because ticking one box is rarely the whole intent, and
                hideSelectedOptions={false} because a checkbox that vanishes when ticked cannot be
                unticked. */}
            <Select
              aria-label="Status"
              isMulti
              value={STATUS_OPTIONS.filter(o => selectedStatuses.includes(o.value))}
              options={STATUS_OPTIONS}
              onChange={(opts) => {
                const next = { ...filters, status: serialiseStatusFilter((opts ?? []).map(o => o.value)) }
                onFiltersChange(next)
                onFiltersBlur?.(next)
              }}
              components={{ Option: StatusOption }}
              styles={statusSelectStyles}
              menuPortalTarget={document.body}
              isSearchable={false}
              isClearable={false}
              backspaceRemovesValue={false}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              controlShouldRenderValue={false}
              placeholder={statusFilterLabel(selectedStatuses)}
            />
          </div>

          {/* Radius (mi) — relocated here from Search Criteria */}
          <div className="flex flex-col relative shrink-0">
            <span className={labelCls}>Radius (mi)</span>
            <input
              type="number" min="0.4" max="1" step="0.1"
              aria-label="Radius (mi)"
              value={criteria?.radiusMiles ?? ''}
              onChange={setCriteria('radiusMiles')}
              onKeyDown={blockSpecialNumberKeys}
              onPaste={blockSpecialNumberPaste}
              onDrop={blockSpecialNumberDrop}
              onBlur={() => onCriteriaBlur?.(criteria)}
              className={`${numberFieldCls} ${fieldBorderCls(radiusError)}`}
            />
            <FieldError message={shownErrorField === 'radius' ? radiusError : null} />
          </div>

          {/* Time (mo) — relocated here, formerly "Find Comps Within" */}
          <div className="flex flex-col relative shrink-0">
            <span className={labelCls}>Time (mo)</span>
            <input
              type="number" min="3" max="12"
              aria-label="Time (mo)"
              value={criteria?.monthsWithin ?? ''}
              onChange={setCriteria('monthsWithin')}
              onKeyDown={blockSpecialNumberKeys}
              onPaste={blockSpecialNumberPaste}
              onDrop={blockSpecialNumberDrop}
              onBlur={() => onCriteriaBlur?.(criteria)}
              className={`${numberFieldCls} ${fieldBorderCls(timeError)}`}
            />
            <FieldError message={shownErrorField === 'time' ? timeError : null} />
          </div>

          {/* Price (000s) */}
          <div className="flex flex-col shrink-0">
            <span className={labelCls}>Price (000s)</span>
            <div className="flex items-center gap-x-[3px]">
              <input
                type="number"
                aria-label="Price min"
                value={filters.priceMin ?? ''}
                onChange={set('priceMin')}
                onKeyDown={blockSpecialNumberKeys}
                onPaste={blockSpecialNumberPaste}
                onDrop={blockSpecialNumberDrop}
                onBlur={() => onFiltersBlur?.(filters)}
                placeholder="Min"
                className={`${numberFieldCls} ${fieldBorderCls(null)}`}
              />
              <span className="text-[14px] font-medium text-brand-dark">-</span>
              <input
                type="number"
                aria-label="Price max"
                value={filters.priceMax ?? ''}
                onChange={set('priceMax')}
                onKeyDown={blockSpecialNumberKeys}
                onPaste={blockSpecialNumberPaste}
                onDrop={blockSpecialNumberDrop}
                onBlur={() => onFiltersBlur?.(filters)}
                placeholder="Max"
                className={`${numberFieldCls} ${fieldBorderCls(null)}`}
              />
            </div>
          </div>

          {/* Sqft */}
          <div className="flex flex-col shrink-0">
            <span className={labelCls}>Sqft</span>
            <div className="flex items-center gap-x-[3px]">
              <input
                type="number"
                aria-label="Sqft min"
                value={filters.sqftMin ?? ''}
                onChange={set('sqftMin')}
                onKeyDown={blockSpecialNumberKeys}
                onPaste={blockSpecialNumberPaste}
                onDrop={blockSpecialNumberDrop}
                onBlur={() => onFiltersBlur?.(filters)}
                placeholder="Min"
                className={`${numberFieldCls} ${fieldBorderCls(null)}`}
              />
              <span className="text-[14px] font-medium text-brand-dark">-</span>
              <input
                type="number"
                aria-label="Sqft max"
                value={filters.sqftMax ?? ''}
                onChange={set('sqftMax')}
                onKeyDown={blockSpecialNumberKeys}
                onPaste={blockSpecialNumberPaste}
                onDrop={blockSpecialNumberDrop}
                onBlur={() => onFiltersBlur?.(filters)}
                placeholder="Max"
                className={`${numberFieldCls} ${fieldBorderCls(null)}`}
              />
            </div>
          </div>

        </span>

        {/* Comment Set #1, ARV 9 — the `N Results` pill is gone. It was ours: the export has no
            Results node anywhere in `filters-table`, and his reason is that Analysis Complete
            already says it.

            The pill printed baseFiltered.length, the comps left standing by this row. Analysis
            Complete's own collapsed count used to print comps.length instead — the size of the
            analysis before any of these fields touched it — so removing the pill briefly left no
            node on the page naming the filtered total outside the Show field's `All N` option (and
            its own label once the filtered set drops below ten, since normaliseShow then reads
            `All 5` rather than `Top 10`). SearchProgress.jsx now takes the same baseFiltered.length
            this row's Show field carries, so the two agree again — see that file's own comment on
            the collapsed return block for the current reasoning.

            The Included/Excluded count chips that used to sit here moved out to
            IncludedExcludedToggle.jsx, rendered above the ARV Estimate card — this row is filter
            fields only now. */}

      </div>

    </div>
  )
}
